import { createContext, useContext, useRef, useCallback, useSyncExternalStore, useEffect } from "react";
import _ from "lodash";

let globalStores = {};

const createStore = (initialState) => {
  let state = initialState;
  const listeners = new Set();

  return {
    getState: () => state,
    setState: (nextState) => {
      if (!_.isEqual(state, nextState)) {
        state = nextState;
        listeners.forEach((l) => l());
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
};

const MetaStateContext = createContext(null);

export const MetaStateProvider = ({ children, state }) => {
  const storeRef = useRef(createStore(state));

  useEffect(() => {
    storeRef.current.setState(state);
  }, [state]);

  useEffect(() => {
    const key = state.primaryField;
    if (key) {
      globalStores[key] = storeRef.current;
    }
    return () => {
      if (key) {
        delete globalStores[key];
      }
    };
  }, [state.primaryField]);

  return (
    <MetaStateContext.Provider value={storeRef.current}>
      {children}
    </MetaStateContext.Provider>
  );
};

export const useMetaState = (field) => {
  const store = useContext(MetaStateContext);

  const subscribe = useCallback((cb) => store.subscribe(cb), [store]);
  const getSnapshot = useCallback(() => field ? _.get(store.getState(), field) : store.getState(), [store, field]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const getMetaStoreState = (key) => {
  if (!key) {
    console.error(`MetaStateContext - getMetaStoreState: Error catched: Parameter "Key" is required.`);
    return null;
  }
  if (!globalStores[key]) {
    console.error(`MetaStateContext - getMetaStoreState: Error catched: MetaStore with key parameter "${key}" not found.`);
    return null;
  }
  return globalStores[key].getState();
};

export default MetaStateContext;
